<template>
  <div class="async-select-wrapper">
    <div class="select-container" :class="{ 'is-open': isOpen, 'is-disabled': disabled }">
      <!-- Selected Value Display -->
      <div class="select-trigger" @click="toggleDropdown" ref="trigger">
        <span v-if="selectedBranch" class="selected-value">{{ selectedBranch.name }}</span>
        <span v-else class="select-placeholder">{{ placeholder }}</span>
        <i class="fas fa-chevron-down dropdown-icon" :class="{ 'rotated': isOpen }"></i>
      </div>

      <!-- Dropdown Menu -->
      <transition name="dropdown">
        <div v-if="isOpen" class="dropdown-menu" ref="dropdown">
          <!-- Search Input -->
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              @input="handleSearch"
              placeholder="Search branches..."
              class="search-input"
              ref="searchInput"
            />
            <i v-if="searchQuery" class="fas fa-times clear-icon" @click="clearSearch"></i>
          </div>

          <!-- Options List -->
          <div class="options-list" @scroll="handleScroll" ref="optionsList">
            <!-- Loading State -->
            <div v-if="isLoading && branches.length === 0" class="loading-state">
              <i class="fas fa-spinner fa-spin"></i>
              <span>Loading branches...</span>
            </div>

            <!-- Options -->
            <div
              v-for="branch in branches"
              :key="branch.id"
              class="option-item"
              :class="{ 'is-selected': modelValue === branch.id }"
              @click="selectBranch(branch)"
            >
              <span class="option-text">{{ branch.name }}</span>
              <i v-if="modelValue === branch.id" class="fas fa-check check-icon"></i>
            </div>

            <!-- Load More Indicator -->
            <div v-if="isLoadingMore" class="loading-more">
              <i class="fas fa-spinner fa-spin"></i>
              <span>Loading more...</span>
            </div>

            <!-- No Results -->
            <div v-if="!isLoading && branches.length === 0" class="no-results">
              <i class="fas fa-inbox"></i>
              <span>No branches found</span>
            </div>

            <!-- End of List -->
            <div v-if="!hasMore && branches.length > 0 && !isLoading" class="end-of-list">
              <span>No more branches</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import api from '@/helpers/api'

export default {
  name: 'AsyncBranchSelect',

  props: {
    modelValue: {
      type: String,
      default: ''
    },
    corporateId: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Select Branch'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const isOpen = ref(false)
    const searchQuery = ref('')
    const branches = ref([])
    const isLoading = ref(false)
    const isLoadingMore = ref(false)
    const currentPage = ref(1)
    const hasMore = ref(true)
    const searchTimeout = ref(null)
    const trigger = ref(null)
    const dropdown = ref(null)
    const searchInput = ref(null)
    const optionsList = ref(null)
    
    // Cache untuk menyimpan hasil search
    const cache = ref({})
    const lastSearchQuery = ref('')

    const initialBranchName = ref('')

    const selectedBranch = computed(() => {
      const found = branches.value.find(b => b.id === props.modelValue)
      if (found) return found
      if (initialBranchName.value && props.modelValue) {
        return { id: props.modelValue, name: initialBranchName.value }
      }
      return null
    })

    const fetchInitialBranch = async () => {
      if (!props.modelValue) return
      try {
        const response = await api.request('get', `/api/extranet/branches/${props.modelValue}`)
        if (response.data?.data) {
          initialBranchName.value = response.data.data.name
        }
      } catch (err) {
        console.error('[AsyncBranchSelect] Error fetching initial branch:', err)
      }
    }

    const toggleDropdown = () => {
      if (props.disabled) return
      
      isOpen.value = !isOpen.value
      
      if (isOpen.value) {
        // Focus search input when dropdown opens
        setTimeout(() => {
          searchInput.value?.focus()
        }, 100)
        
        // Load branches if not loaded yet
        if (branches.value.length === 0) {
          loadBranches()
        }
      }
    }

    const closeDropdown = () => {
      isOpen.value = false
    }

    const handleClickOutside = (event) => {
      if (
        trigger.value &&
        dropdown.value &&
        !trigger.value.contains(event.target) &&
        !dropdown.value.contains(event.target)
      ) {
        closeDropdown()
      }
    }

    const loadBranches = async (reset = false) => {
      if (reset) {
        currentPage.value = 1
        branches.value = []
        hasMore.value = true
      }

      if (isLoading.value || isLoadingMore.value || !hasMore.value) return

      // Generate cache key
      const cacheKey = `${props.corporateId}_${searchQuery.value}_${currentPage.value}`
      
      // Check cache first
      if (cache.value[cacheKey]) {
        const cachedData = cache.value[cacheKey]
        
        if (reset) {
          branches.value = cachedData.branches
        } else {
          branches.value = [...branches.value, ...cachedData.branches]
        }
        
        hasMore.value = cachedData.has_more
        currentPage.value++
        return
      }

      if (currentPage.value === 1) {
        isLoading.value = true
      } else {
        isLoadingMore.value = true
      }

      try {
        const params = {
          custom: {
            page: currentPage.value,
            limit: 10,
            corporate_id: props.corporateId
          }
        }

        if (searchQuery.value) {
          params.custom.search = searchQuery.value
        }

        const response = await api.request('get', '/api/extranet/branches/dropdown', params)
        const data = response.data.data

        // Store in cache
        cache.value[cacheKey] = {
          branches: data.branches,
          has_more: data.pagination.has_more
        }

        if (reset) {
          branches.value = data.branches
        } else {
          branches.value = [...branches.value, ...data.branches]
        }

        hasMore.value = data.pagination.has_more
        currentPage.value++
      } catch (error) {
        console.error('[AsyncBranchSelect] Error loading branches:', error)
      } finally {
        isLoading.value = false
        isLoadingMore.value = false
      }
    }

    const handleSearch = () => {
      // Debounce search - hanya hit API setelah user berhenti mengetik 500ms
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }

      searchTimeout.value = setTimeout(() => {
        // Hanya load jika query berubah
        if (searchQuery.value !== lastSearchQuery.value) {
          lastSearchQuery.value = searchQuery.value
          loadBranches(true)
        }
      }, 500) // Increased to 500ms untuk lebih efisien
    }

    const clearSearch = () => {
      searchQuery.value = ''
      lastSearchQuery.value = ''
      loadBranches(true)
    }

    const handleScroll = () => {
      const list = optionsList.value
      if (!list) return

      const scrollTop = list.scrollTop
      const scrollHeight = list.scrollHeight
      const clientHeight = list.clientHeight

      // Load more when scrolled to bottom (with 50px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore.value && !isLoadingMore.value) {
        loadBranches()
      }
    }

    const selectBranch = (branch) => {
      emit('update:modelValue', branch.id)
      closeDropdown()
    }

    // Watch for corporate_id changes
    watch(() => props.corporateId, () => {
      branches.value = []
      currentPage.value = 1
      hasMore.value = true
      cache.value = {} // Clear cache when corporate changes
      lastSearchQuery.value = ''
      if (isOpen.value) {
        loadBranches(true)
      }
    })

    // Watch for modelValue changes to fetch name if it changes externally
    watch(() => props.modelValue, (newVal) => {
      if (newVal && !branches.value.find(b => b.id === newVal)) {
        fetchInitialBranch()
      }
    })

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      if (props.modelValue) {
        fetchInitialBranch()
      }
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    return {
      isOpen,
      searchQuery,
      branches,
      isLoading,
      isLoadingMore,
      hasMore,
      selectedBranch,
      trigger,
      dropdown,
      searchInput,
      optionsList,
      toggleDropdown,
      closeDropdown,
      handleSearch,
      clearSearch,
      handleScroll,
      selectBranch
    }
  }
}
</script>

<style scoped>
.async-select-wrapper {
  position: relative;
  width: 100%;
}

.select-container {
  position: relative;
}

.select-container.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 48px;
  line-height: 1.4;
}

.select-container:not(.is-disabled) .select-trigger:hover {
  border-color: #cbd5e0;
}

.select-container.is-open .select-trigger {
  border-color: #b51a1f; /* Hardcoded fallback for Extranet Primary */
  border-color: var(--extranet-primary);
  box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.1);
}

.selected-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-placeholder {
  font-size: 14px;
  color: #94a3b8;
  user-select: none;
  background: transparent !important; /* Force transparent to override Bootstrap helper */
}

.dropdown-icon {
  font-size: 12px;
  color: #718096;
  transition: transform 0.2s;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.search-box {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.search-icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #a0aec0;
}

.search-input {
  width: 100%;
  padding: 10px 36px 10px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #b51a1f;
  border-color: var(--extranet-primary);
  box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.1);
}

.clear-icon {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #a0aec0;
  cursor: pointer;
  transition: color 0.2s;
}

.clear-icon:hover {
  color: #718096;
}

.options-list {
  overflow-y: auto;
  max-height: 240px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.option-item:hover {
  background: #f7fafc;
}

.option-item.is-selected {
  background: #fff5f5;
  color: #b51a1f;
  color: var(--extranet-primary);
}

.option-text {
  font-size: 14px;
  font-weight: 500;
}

.check-icon {
  font-size: 14px;
  color: #b51a1f;
  color: var(--extranet-primary);
}

.loading-state,
.loading-more,
.no-results,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  font-size: 14px;
  color: #718096;
}

.loading-state i,
.loading-more i {
  font-size: 16px;
}

.no-results {
  flex-direction: column;
  gap: 12px;
  padding: 32px 16px;
}

.no-results i {
  font-size: 32px;
  color: #cbd5e0;
}

.end-of-list {
  padding: 12px;
  font-size: 12px;
  color: #a0aec0;
  border-top: 1px solid #e2e8f0;
}

/* Dropdown Transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
